import axios from "axios";
import {
    getCurrentEnvironmentConfig,
    logger,
    objectToQueryParams,
    deleteNativeCookies,
    buildRedirectPath,
    snooze,
    // setCookie
} from "../util/AppUtil";
import Auth from '@aws-amplify/auth';
import {SOCIAL_SIGNIN_DOMAIN} from '../util/AppConstant';
import queryString from 'query-string';
import { configureAmplifyAuth } from './AmplifyConfig';
// import { getCurrentEnv } from '@sfly/uig-current-environment-util';

const APP_SERVER_RETRY_TIMEOUT = 200;
const APP_SERVER_RETRY_ATTEMPT = 2;
const APP_CENTRAL_CONFIG_EXPIRE_TIME = 300000; // 5 minutes


const getCachedCentralConfig = () => {
    const centralConfig = localStorage.getItem('centralConfig');
    if (centralConfig) {
        const config = JSON.parse(centralConfig);
        if (config.expires_in > Date.now()) {
            return config;
        }
    }
    return null;
};

const cacheCentralConfig = (config) => {
    config.expires_in = new Date().getTime() + APP_CENTRAL_CONFIG_EXPIRE_TIME;
    localStorage.setItem('centralConfig', JSON.stringify(config));
};

/**
 * See /scripts/CentralConfig-for-sfly-sso-fe.postman_collection.json for scripts to change these flags
 * @returns {Promise<any>}
 */
export const getCentralConfig = () => {
    const config = {
        headers: {
            'sfly-ccs-api-key': getCurrentEnvironmentConfig().ccs.key
        }
    };
    const centralConfig = getCachedCentralConfig();
    if (centralConfig) {
        return centralConfig;
    } else {
        return new Promise((resolve => {
            const returnValue = {
                show_downtime_msg: false,
                show_downtime_banner: false,
                show_downtime_reset_pw: false,
                socialEnabled: true,
                wyngsupport: false,
                is_new_dynamic_content: true,
                show_old_discount_for_costco: false,
                is_new_preschool_url: true
            };
            axios.get(getCurrentEnvironmentConfig().ccs.url, config)
                .then((result) => {
                    cacheCentralConfig(result.data);
                    resolve(result.data);
                })
                .catch((e) => {
                    logger.error({
                        appContext: {
                            msg: 'ERROR LOADING CENTRAL CONFIG SERVICE',
                            error: e
                        }
                    });
                    resolve(returnValue);
                });
        }));
    }
};

/**
 * Gets gets user pool config data from User service endpoint
 */
export const getAwsConfig = (optInVal) => {
    return new Promise((resolve) => {
        configureAmplifyAuth(getCurrentEnvironmentConfig().iam, optInVal);
        resolve(getCurrentEnvironmentConfig().iam);
    });
};

/**
 * Checks if appserver session is valid, if it is not valid establish appserver session.
 * @param token - Refresh token
 */
export const validateAndStartAppServerState = (token) => {
    const config = { withCredentials: true };
    let baseUrl = _getServerSessionBaseUrl();

    return new Promise((resolve, reject) => {
        axios.get(`${baseUrl}/userstate/query.sfly`, config)
        .then(async (result) => {
            if(result.data.expires_in === 0) {
                const error = await startAppServerSession(token, null, baseUrl);
                if (error) {
                    logger.error({
                        appContext: {
                            msg: 'validateAndStartAppServerState: appServer exception',
                            error: JSON.stringify(error.errorObject)
                        }
                    });
                    // Log user out and clean up current session (USER-3826)
                    reject(error);
                    return;
                }
            }
            resolve();
        })
        .catch((error) => {
            logger.error({
                appContext: {
                    msg: 'validateAndStartAppServerState',
                    error: JSON.stringify(error)
                }
            });
            resolve();
        });
    });
};

/**
 * Starts appserver after successful signin and signup.
 * @param {*} token 
 * @param {*} payload
 * @param {string} tpBaseUrl
 * @param {string} redirectUri
 */
export const startAppServerSession = (token, payload, baseUrl, redirectUri, withCredentials) => {
    const data = {
        ...payload,
        refreshToken: token
    };
    const retryAppServerSession = shouldRetryAppServerSession(redirectUri);
    const retryCount = retryAppServerSession ? APP_SERVER_RETRY_ATTEMPT : 0;
    const config = { withCredentials: !withCredentials ? true : false };
    baseUrl = baseUrl ? baseUrl : _getServerSessionBaseUrl();

    // HACK ALERT: We need to start both sessions when the redirectUri contains tinyprints.com.
    // This can probably be removed once we are 100% on IP2
    if(baseUrl === getCurrentEnvironmentConfig().tpBaseUrl) {
        // fire and forget this one.
        const endpoint = `${getCurrentEnvironmentConfig().wwwBaseUrl}/rest/refreshtoken/authorize`;
        startAppServerSessionHelper(endpoint, data, config, retryCount);
    }
    // END HACK ALERT:

    return startAppServerSessionHelper(`${baseUrl}/rest/refreshtoken/authorize`, data, config, retryCount)
        .then((error) => {
            if (retryAppServerSession) {
                // Return the result. Caller should not redirect
                // on error because app server session is mandatory.
                return error;
            }
            return null;
        });
};

const startAppServerSessionHelper = (url, data, config, retryCount) => {
    return axios.post(url, data, config)
        .then(() => {
            console.log("authorize success")
            //Below code is commented as part of USR-3603
            // const cookieName = getCurrentEnv() === 'production' ? 'refreshSession' : `refreshSession_${getCurrentEnv()}`;
            // const d = new Date();
            // // Set cookie to 30 min
            // d.setTime(d.getTime() + (30 * 60 * 1000));
            // let host = window.location.hostname;
            // const match = host.match(/(dev|beta|stage|kappa)/i);
            // if (match) {
            //     host = '.' + host.substring(match.index);
            // } else if (host.indexOf('.shutterfly.com')) {
            //     host = '.shutterfly.com';
            // } else if (host.indexOf('.tinyprints.com')) {
            //     host = '.tinyprints.com';
            // }
            // setCookie(cookieName, 'true', d, host);
        })
        .catch((exception) => {
            if (exception.response) {
                const status = exception.response.status;
                // For 5xx error, retry 3 times and ignore
                if (status >= 500) {
                    if (retryCount > 0) {
                        return snooze(APP_SERVER_RETRY_TIMEOUT).then(() => startAppServerSessionHelper(url, data, config, retryCount - 1));
                    }
                    return null;
                }
                // For 401 or 403, return error
                if ([401, 403].indexOf(status) >= 0) {
                    exception = { response: Object.assign({}, exception.response) };
                    delete exception.response.config;   // remove refresh token from log
                    let message = '';
                    if (exception.response.data && exception.response.data.message) {
                        message = exception.response.data.message;
                    }
                    return {
                        errorObject: exception,
                        message,
                    };
                }
            }
            // ignore others
            return null;
        });
};

/**
  * Retry logic to the starting of the app server.
  * Condition: redirectUri contains "shutterfly.com" and has a resource extension of ".jsp" or ".sfly"
  * 
  * @param {*} redirectUri 
  */
 export const shouldRetryAppServerSession = (redirectUri) => {
    if (!redirectUri) {
        return true;
    }
   return redirectUri.match(/\.shutterfly\.com/) !== null && redirectUri.match(/(\.jsp|\.sfly)/) !== null;
};

export const handleAppServerSessionError = async () => {
    try {
        await Auth.signOut();
    }
    catch(e) {
    }
    let queryStringParams = queryString.parse(window.location.search);
    const params = Object.assign({}, queryStringParams, { errorCode: 'AppServerSession' });
    if (window && window.location) {
        window.location.assign(buildRedirectPath(params));
    }
};

export const callSocialEndpoint = (queryStringParams, appClientId, idToken) => {
    let availableIdp = queryStringParams.availableIdp;
    let challengeName = queryStringParams.challengeName;
    let session = queryStringParams.session;

    //deleting the session from the query params since we need to preserve rest of them to be handled in callback.
    delete queryStringParams.availableIdp;
    delete queryStringParams.challengeName;
    delete queryStringParams.session;
    window.localStorage.setItem('sflySsoQueryParams', objectToQueryParams(queryStringParams));
    let socialProvider = window.localStorage.getItem('socialProvider');
    let payload = {
        identity_provider: socialProvider,
        selectedIdp: availableIdp,
        challengeName: challengeName,
        redirect_uri: window.location.origin + '/social/callback',
        response_type: 'TOKEN',
        client_id: appClientId,
        scope: 'email openid phone profile',
        session: session,
        id_token: idToken
    };
    payload = queryString.stringify(payload);
    let url = 'https://' + SOCIAL_SIGNIN_DOMAIN + '/oauth2/authorize?' + payload;
    deleteNativeCookies();
    window.location.assign(url);
};

let _getServerSessionBaseUrl = () => {
    let queryStringParams = queryString.parse(window.location.search);
    let redirectUri = queryStringParams.redirectUri;
    let baseUrl = getCurrentEnvironmentConfig().wwwBaseUrl;
    
    // For redirectUri, If it is TP, start a tp/ share start their respective session, if not start it with shutterfly.com
    if(redirectUri) {
        if (redirectUri.indexOf('tinyprints.com') > -1) {
            baseUrl = getCurrentEnvironmentConfig().tpBaseUrl;
        } else if (redirectUri.indexOf('share.') > -1) {
            baseUrl = getCurrentEnvironmentConfig().shareBaseUrl;
        }
    }
    return baseUrl;
};
